import React, { useEffect, useMemo, useState } from 'react';
import { auth, db } from '../../firebase/config';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { useAppSelector } from '../../store';
import Table, { Column } from '../../components/Table';
import Modal from '../../components/Modal';
import Icon from '../../components/Icon';
import { FiX, FiSearch } from 'react-icons/fi';
import { fetchCompanyIdAndPsoInfo, fetchApprovedTemporaryGuardLookupForBank, lookupTemporaryGuardCount } from '../request_management/request_form/service/requestManagementService';
import { branchTypeKpiOrderedRows, countBranchesByType, fetchBranchBankId, branchTypeKpiLabel } from '../branches/service/branchesService';
import { Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

type RangeKey = 'daily' | 'weekly' | 'monthly' | 'all';

type BranchSurveyRow = {
    id: string;
    date: string;
    dateTs: number | null;
    surveyId: string;
    branchCode: string;
    psoName: string;
    location: string;
    status: string;
};

type SummaryRow = {
    id: string;
    date: string;
    dateTs: number | null;
    psoName: string;
    location: string;
    totalBranches: number;
    branchSurveys: number;
    newLocationSurveys: number;
};

type DashboardData = {
    kpis: {
        branches: number;
        branchesByType: Record<string, number>;
        psos: number;
        regions: number;
        requests: number;
        staffTotal: number;
        staffPso: number;
        staffIncharges: number;
        staffRegional: number;
    };
    requestStatus: Record<string, number>;
    branchSurveyRows: BranchSurveyRow[];
    summaryRows: SummaryRow[];
    rawBranches: any[];
    rawPsos: any[];
    rawLocs: any[];
};

const CACHE_TTL_MS = 15 * 60 * 1000;
const memoryCache = new Map<string, { at: number; data: DashboardData }>();

const BRANCH_TYPE_KPI_GRADIENTS = [
    'from-sky-500 to-sky-600',
    'from-indigo-500 to-indigo-600',
    'from-violet-500 to-violet-600',
    'from-fuchsia-500 to-fuchsia-600',
    'from-amber-500 to-amber-600',
    'from-emerald-500 to-emerald-600',
    'from-cyan-500 to-cyan-600',
    'from-rose-500 to-rose-600',
] as const;

export type HeadDashboardProps = {
    /** When provided (including `[]`), KPIs and tables are scoped to these PSO auth / doc IDs (Regional PSO / Regional Head). Omit for full-bank Head view. */
    managedPsoAuthIds?: string[];
    dashboardTitle?: string;
    dashboardSubtitle?: string;
    hideStaffKpis?: boolean;
};

async function fetchDocsCreatedByInChunks(
    bankId: string,
    collectionName: 'visit' | 'locationSurveys',
    ids: string[]
): Promise<any[]> {
    const unique = Array.from(new Set(ids.map(String).filter(Boolean)));
    const allDocs: any[] = [];
    for (let i = 0; i < unique.length; i += 10) {
        const chunk = unique.slice(i, i + 10);
        if (!chunk.length) continue;
        const snap = await getDocs(query(collection(db, 'banks', bankId, collectionName), where('createdBy', 'in', chunk)));
        allDocs.push(...snap.docs);
    }
    return allDocs;
}

function isPsoDesignation(designation: unknown): boolean {
    const value = String(designation || '')
        .replace(/_/g, ' ')
        .trim()
        .toLowerCase();
    if (!value) return true;
    if (value.includes('head') || value.includes('regional')) return false;
    return value === 'pso';
}

function isInchargeDesignation(designation: unknown): boolean {
    const value = String(designation || '')
        .replace(/_/g, ' ')
        .trim()
        .toLowerCase();
    return value.includes('incharge');
}

function isRegionalDesignation(designation: unknown): boolean {
    const value = String(designation || '')
        .replace(/_/g, ' ')
        .trim()
        .toLowerCase();
    return value.includes('regional');
}

function rangeStart(range: RangeKey): Date | null {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (range === 'daily') return start;
    if (range === 'weekly') {
        start.setDate(start.getDate() - 6);
        return start;
    }
    if (range === 'monthly') {
        start.setDate(start.getDate() - 29);
        return start;
    }
    return null;
}

function rangeEnd(range: RangeKey): Date | null {
    if (range === 'all') return null;
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
}

function toDateValue(v: any): Date | null {
    if (!v) return null;
    if (v?.toDate) return v.toDate();
    if (typeof v === 'number') {
        const d = new Date(v);
        return Number.isNaN(d.getTime()) ? null : d;
    }
    if (typeof v === 'object') {
        // Support serialized Firestore timestamps: { seconds, nanoseconds } / { _seconds, _nanoseconds }
        const seconds = typeof v.seconds === 'number' ? v.seconds : (typeof v._seconds === 'number' ? v._seconds : null);
        if (seconds !== null) {
            const nanos =
                typeof v.nanoseconds === 'number'
                    ? v.nanoseconds
                    : (typeof v._nanoseconds === 'number' ? v._nanoseconds : 0);
            const ms = seconds * 1000 + Math.floor(nanos / 1_000_000);
            const d = new Date(ms);
            return Number.isNaN(d.getTime()) ? null : d;
        }
    }
    if (typeof v === 'string') {
        const s = v.trim();
        // dd-mm-yyyy / dd/mm/yyyy
        const dmy = s.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
        if (dmy) {
            const dd = Number(dmy[1]);
            const mm = Number(dmy[2]) - 1;
            const yyyy = Number(dmy[3]);
            const parsed = new Date(yyyy, mm, dd);
            if (!Number.isNaN(parsed.getTime())) return parsed;
        }
        // yyyy-mm-dd / yyyy/mm/dd
        const ymd = s.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
        if (ymd) {
            const yyyy = Number(ymd[1]);
            const mm = Number(ymd[2]) - 1;
            const dd = Number(ymd[3]);
            const parsed = new Date(yyyy, mm, dd);
            if (!Number.isNaN(parsed.getTime())) return parsed;
        }
    }
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
}

/** Calendar date in the user's local timezone (not UTC), so charts like Weekly Survey Summary align with "today". */
function dateOnly(d: Date | null): string {
    if (!d) return 'N/A';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

export default function HeadDashboard({
    managedPsoAuthIds,
    dashboardTitle = 'Head Dashboard',
    dashboardSubtitle,
    hideStaffKpis = false,
}: HeadDashboardProps = {}) {
    const pso = useAppSelector((s) => s.user.pso);
    const [companyId, setCompanyId] = useState<string | null>(null);
    const [branchTab, setBranchTab] = useState<RangeKey>('daily');
    const [summaryTab, setSummaryTab] = useState<RangeKey>('daily');
    const [branchPage, setBranchPage] = useState(1);
    const [selectedKpi, setSelectedKpi] = useState<{
        type: 'regions' | 'pso' | 'incharges' | 'regional_head' | 'branch_type';
        label: string;
    } | null>(null);
    const [modalSearch, setModalSearch] = useState('');
    const [modalPage, setModalPage] = useState(1);
    const [summaryPage, setSummaryPage] = useState(1);
    const [summarySearchPso, setSummarySearchPso] = useState('all');
    const [summarySearchLocation, setSummarySearchLocation] = useState('all');
    const [summaryPsoOptions, setSummaryPsoOptions] = useState<string[]>([]);
    const [summaryLocationOptions, setSummaryLocationOptions] = useState<string[]>([]);
    const [summaryQueryRows, setSummaryQueryRows] = useState<SummaryRow[] | null>(null);
    const [summaryPsoBranchCounts, setSummaryPsoBranchCounts] = useState<Record<string, number>>({});
    const [summaryPsoLocations, setSummaryPsoLocations] = useState<Record<string, string>>({});
    const [summarySortBy, setSummarySortBy] = useState<'totalBranches' | 'branchSurveys' | 'newLocationSurveys' | null>('branchSurveys');
    const [summarySortOrder, setSummarySortOrder] = useState<'asc' | 'desc'>('desc');
    const [coverageMonth, setCoverageMonth] = useState<string>(() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    });
    const [loadingKpi, setLoadingKpi] = useState(true);
    const [loadingCharts, setLoadingCharts] = useState(true);
    const [loadingTables, setLoadingTables] = useState(true);
    const [data, setData] = useState<DashboardData>({
        kpis: {
            branches: 0,
            branchesByType: {},
            psos: 0,
            regions: 0,
            requests: 0,
            staffTotal: 0,
            staffPso: 0,
            staffIncharges: 0,
            staffRegional: 0,
        },
        requestStatus: {},
        branchSurveyRows: [],
        summaryRows: [],
        rawBranches: [],
        rawPsos: [],
        rawLocs: [],
    });
    const [guardTempLookup, setGuardTempLookup] = useState<any>(null);

    useEffect(() => {
        if (!companyId) {
            setGuardTempLookup(null);
            return;
        }
        let cancelled = false;
        void (async () => {
            try {
                const lk = await fetchApprovedTemporaryGuardLookupForBank(companyId);
                if (!cancelled) setGuardTempLookup(lk);
            } catch (e) {
                console.warn('Temporary guard lookup failed:', e);
                if (!cancelled) setGuardTempLookup(null);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [companyId]);

    const managedScopeKey = managedPsoAuthIds === undefined ? 'head' : [...managedPsoAuthIds].map(String).sort().join('|');

    useEffect(() => {
        const uid = auth.currentUser?.uid;
        if (!uid) return;
        const run = async () => {
            let resolvedCompanyId: string | null = null;
            try {
                const info = await fetchCompanyIdAndPsoInfo(uid);
                resolvedCompanyId = info?.companyId || null;
            } catch (e) {
                console.error('HeadDashboard company resolve failed (primary):', e);
            }
            if (!resolvedCompanyId) {
                try {
                    resolvedCompanyId = await fetchBranchBankId(uid);
                } catch (e) {
                    console.error('HeadDashboard company resolve failed (fallback):', e);
                }
            }
            if (!resolvedCompanyId) return;
            setCompanyId(resolvedCompanyId);

            const managedSet = managedPsoAuthIds === undefined ? null : new Set((managedPsoAuthIds || []).map(String));
            const cacheKey = managedSet
                ? `${resolvedCompanyId}:regional:${Array.from(managedSet).sort().join(',')}:v2`
                : `${resolvedCompanyId}:all:v2`;
            const cached = memoryCache.get(cacheKey);
            if (cached && Date.now() - cached.at < CACHE_TTL_MS) {
                setData({
                    ...cached.data,
                    rawBranches: cached.data.rawBranches || [],
                    rawPsos: cached.data.rawPsos || [],
                    rawLocs: cached.data.rawLocs || [],
                });
                setLoadingKpi(false);
                setLoadingCharts(false);
                setLoadingTables(false);
                return;
            }

            setLoadingKpi(true);
            setLoadingCharts(true);
            setLoadingTables(true);

            const reqWhere: any[] = [];
            const surveyWhere: any[] = [];

            const visitPromise =
                managedSet === null
                    ? getDocs(query(collection(db, 'banks', resolvedCompanyId, 'visit'), ...surveyWhere))
                    : managedSet.size === 0
                      ? Promise.resolve({ docs: [] as any[] } as Awaited<ReturnType<typeof getDocs>>)
                      : (async () => {
                            const docs = await fetchDocsCreatedByInChunks(resolvedCompanyId, 'visit', Array.from(managedSet));
                            return { docs } as Awaited<ReturnType<typeof getDocs>>;
                        })();

            const locationSurveyPromise =
                managedSet === null
                    ? getDocs(query(collection(db, 'banks', resolvedCompanyId, 'locationSurveys'), ...surveyWhere))
                    : managedSet.size === 0
                      ? Promise.resolve({ docs: [] as any[] } as Awaited<ReturnType<typeof getDocs>>)
                      : (async () => {
                            const docs = await fetchDocsCreatedByInChunks(resolvedCompanyId, 'locationSurveys', Array.from(managedSet));
                            return { docs } as Awaited<ReturnType<typeof getDocs>>;
                        })();

            const [branchSnapRes, psoSnapRes, locSnapRes, visitSnapRes, locationSurveySnapRes, requestsSnapRes] = await Promise.allSettled([
                getDocs(collection(db, 'banks', resolvedCompanyId, 'branch')),
                getDocs(collection(db, 'banks', resolvedCompanyId, 'pos')),
                getDocs(collection(db, 'locations')),
                visitPromise,
                locationSurveyPromise,
                getDocs(query(collection(db, 'banks', resolvedCompanyId, 'requests'), ...reqWhere)),
            ]);

            let branches = branchSnapRes.status === 'fulfilled' ? branchSnapRes.value.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) : [];
            const psos = psoSnapRes.status === 'fulfilled' ? psoSnapRes.value.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) : [];
            const locs = locSnapRes.status === 'fulfilled' ? locSnapRes.value.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) : [];
            let visits = visitSnapRes.status === 'fulfilled' ? visitSnapRes.value.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) : [];
            let locationSurveys =
                locationSurveySnapRes.status === 'fulfilled' ? locationSurveySnapRes.value.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) : [];
            let requests = requestsSnapRes.status === 'fulfilled' ? requestsSnapRes.value.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) : [];

            if (managedSet) {
                branches = branches.filter((b: any) => managedSet.has(String(b.psoId || '')));
                requests = requests.filter((r: any) => managedSet.has(String(r.createdBy || '')));
                visits = visits.filter((v: any) => managedSet.has(String(v.createdBy || '')));
                locationSurveys = locationSurveys.filter((s: any) => managedSet.has(String(s.createdBy || '')));
            }

            const psosInScope = managedSet
                ? psos.filter((p: any) => managedSet.has(String(p.id)) || managedSet.has(String(p.psoId || '')))
                : psos;

            const branchesByType = countBranchesByType(branches as { branchType?: string; branchTypeOther?: string }[]);
            const staffTotal = psosInScope.length;
            const staffRegional = psosInScope.filter((p: any) => isRegionalDesignation(p.designation)).length;
            const staffIncharges = psosInScope.filter((p: any) => isInchargeDesignation(p.designation)).length;
            const staffPso = psosInScope.filter((p: any) => isPsoDesignation(p.designation) && !isInchargeDesignation(p.designation)).length;
            const kpis = {
                branches: branches.length,
                branchesByType,
                psos: psosInScope.filter((p: any) => isPsoDesignation(p.designation)).length,
                regions: managedSet
                    ? new Set(branches.map((b: any) => String(b.locationId || '')).filter(Boolean)).size
                    : locs.length,
                requests: requests.length,
                staffTotal,
                staffPso,
                staffIncharges,
                staffRegional,
            };
            setData((prev) => ({ ...prev, kpis }));
            setLoadingKpi(false);

            const psoNameById = new Map<string, string>();
            const psoEmailById = new Map<string, string>();
            const psoContactById = new Map<string, string>();
            psos.forEach((p: any) => {
                const name = p.name || 'N/A';
                const email = p.email || 'N/A';
                const contact = p.contact || 'N/A';
                psoNameById.set(String(p.id), name);
                psoEmailById.set(String(p.id), email);
                psoContactById.set(String(p.id), contact);
                if (p.psoId) {
                    psoNameById.set(String(p.psoId), name);
                    psoEmailById.set(String(p.psoId), email);
                    psoContactById.set(String(p.psoId), contact);
                }
            });
            const psoOnlyNames = new Set(
                psosInScope
                    .filter((p: any) => isPsoDesignation(p.designation))
                    .map((p: any) => String(p.name || '').trim())
                    .filter(Boolean)
            );
            const locNameById = new Map<string, string>();
            locs.forEach((l: any) => locNameById.set(String(l.id), l.name || 'N/A'));
            const psoBranchCountByName = new Map<string, number>();
            const psoLocationsByName = new Map<string, Set<string>>();
            branches.forEach((b: any) => {
                const psoName = psoNameById.get(String(b.psoId || '')) || 'N/A';
                if (psoOnlyNames.has(psoName)) {
                    psoBranchCountByName.set(psoName, (psoBranchCountByName.get(psoName) || 0) + 1);
                    const locName = locNameById.get(String(b.locationId || '')) || 'N/A';
                    const existingLocs = psoLocationsByName.get(psoName) || new Set<string>();
                    existingLocs.add(locName);
                    psoLocationsByName.set(psoName, existingLocs);
                }
            });
            setSummaryPsoBranchCounts(Object.fromEntries(psoBranchCountByName.entries()));
            setSummaryPsoLocations(
                Object.fromEntries(
                    Array.from(psoLocationsByName.entries()).map(([name, set]) => [name, Array.from(set).sort((a, b) => a.localeCompare(b)).join(', ') || 'N/A'])
                )
            );
            const branchById = new Map<string, any>();
            branches.forEach((b: any) => branchById.set(String(b.id), b));

            const requestStatus: Record<string, number> = {};
            requests.forEach((r: any) => {
                const s = String(r.status || 'unknown').toLowerCase();
                requestStatus[s] = (requestStatus[s] || 0) + 1;
            });

            const branchSurveyRows: BranchSurveyRow[] = visits.map((v: any) => {
                const b = branchById.get(String(v.branchId || ''));
                const location = b?.locationId ? (locNameById.get(String(b.locationId)) || 'N/A') : 'N/A';
                const date = toDateValue(v.createdAt) || toDateValue(v.dateOfVisit) || toDateValue(v.surveyDate);
                return {
                    id: String(v.id),
                    date: dateOnly(date),
                    dateTs: date ? date.getTime() : null,
                    surveyId: String(v.surveyId || v.id),
                    branchCode: String(b?.branchCode || v.branchCode || 'N/A'),
                    psoName: psoNameById.get(String(v.createdBy || '')) || 'N/A',
                    location,
                    status: String(v.status || 'N/A'),
                };
            });

            const summaryMap = new Map<string, SummaryRow & { branchSet: Set<string> }>();
            const addSummary = (args: {
                dt: string;
                dateTs?: number | null;
                psoName: string;
                location: string;
                branchCode?: string;
                branchSurvey?: number;
                newLocationSurvey?: number;
            }) => {
                if (!psoOnlyNames.has(args.psoName)) return;
                const key = `${args.dt}|${args.psoName}|${args.location}`;
                const ex = summaryMap.get(key) || {
                    id: key,
                    date: args.dt,
                    dateTs: null as number | null,
                    psoName: args.psoName,
                    location: args.location,
                    totalBranches: 0,
                    branchSurveys: 0,
                    newLocationSurveys: 0,
                    branchSet: new Set<string>(),
                };
                ex.branchSurveys += args.branchSurvey || 0;
                ex.newLocationSurveys += args.newLocationSurvey || 0;
                if (args.branchCode) ex.branchSet.add(args.branchCode);
                ex.totalBranches = psoBranchCountByName.get(args.psoName) || 0;
                if (args.dateTs != null) ex.dateTs = args.dateTs;
                summaryMap.set(key, ex);
            };

            branchSurveyRows.forEach((r) =>
                addSummary({
                    dt: r.date,
                    dateTs: r.dateTs,
                    psoName: r.psoName,
                    location: r.location,
                    branchCode: r.branchCode,
                    branchSurvey: 1,
                })
            );

            locationSurveys.forEach((s: any) => {
                const dt = dateOnly(toDateValue(s.createdAt) || toDateValue(s.surveyDate));
                addSummary({
                    dt,
                    dateTs: (() => {
                        const d = toDateValue(s.createdAt) || toDateValue(s.surveyDate);
                        return d ? d.getTime() : null;
                    })(),
                    psoName: psoNameById.get(String(s.createdBy || '')) || 'N/A',
                    location: String(s.locationAddress || 'N/A'),
                    newLocationSurvey: 1,
                });
            });

            const summaryRows = Array.from(summaryMap.values())
                .map(({ branchSet: _branchSet, ...row }) => row)
                .sort((a, b) => b.date.localeCompare(a.date));
            setSummaryPsoOptions(Array.from(psoOnlyNames).sort((a, b) => a.localeCompare(b)));
            setSummaryLocationOptions(Array.from(new Set(locs.map((l: any) => String(l.name || '').trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b)));

            const enrichedBranches = branches.map((b: any) => ({
                ...b,
                locationName: locNameById.get(String(b.locationId || '')) || 'N/A',
                psoName: psoNameById.get(String(b.psoId || '')) || 'N/A',
                psoEmail: psoEmailById.get(String(b.psoId || '')) || 'N/A',
                psoContact: psoContactById.get(String(b.psoId || '')) || 'N/A',
            }));

            const nextData: DashboardData = {
                kpis,
                requestStatus,
                branchSurveyRows,
                summaryRows,
                rawBranches: enrichedBranches,
                rawPsos: psosInScope,
                rawLocs: locs
            };
            setData(nextData);
            setLoadingCharts(false);
            setLoadingTables(false);
            memoryCache.set(cacheKey, { at: Date.now(), data: nextData });
        };
        void run();
    }, [managedScopeKey]);

    useEffect(() => {
        setBranchPage(1);
    }, [branchTab]);

    useEffect(() => {
        setSummaryPage(1);
    }, [summaryTab]);

    const handleSummarySearch = async () => {
        if (!companyId) return;
        const managedSet = managedPsoAuthIds === undefined ? null : new Set((managedPsoAuthIds || []).map(String));
        setLoadingTables(true);
        try {
            const [psoSnap, locSnap, branchSnap] = await Promise.all([
                getDocs(collection(db, 'banks', companyId, 'pos')),
                getDocs(collection(db, 'locations')),
                getDocs(collection(db, 'banks', companyId, 'branch')),
            ]);

            const psos = psoSnap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
            const locs = locSnap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
            let branches = branchSnap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
            if (managedSet) {
                branches = branches.filter((b: any) => managedSet.has(String(b.psoId || '')));
            }
            let psoOnly = psos.filter((p: any) => isPsoDesignation(p.designation));
            if (managedSet) {
                psoOnly = psoOnly.filter((p: any) => managedSet.has(String(p.id)) || managedSet.has(String(p.psoId || '')));
            }
            const psoOnlyNames = new Set(
                psoOnly.map((p: any) => String(p.name || '').trim()).filter(Boolean)
            );

            const selectedPso = summarySearchPso !== 'all' ? summarySearchPso : null;
            const selectedLocation = summarySearchLocation !== 'all' ? summarySearchLocation : null;

            const psoIds = selectedPso
                ? psoOnly
                    .filter((p: any) => String(p.name || '').trim() === selectedPso)
                    .flatMap((p: any) => [String(p.id), String(p.psoId || '')].filter(Boolean))
                : [];

            const locationIds = selectedLocation
                ? locs.filter((l: any) => String(l.name || '').trim() === selectedLocation).map((l: any) => String(l.id))
                : [];

            const locationBranchIds = locationIds.length
                ? branches.filter((b: any) => locationIds.includes(String(b.locationId))).map((b: any) => String(b.id))
                : [];

            const psoNameById = new Map<string, string>();
            psos.forEach((p: any) => {
                const nm = String(p.name || 'N/A');
                psoNameById.set(String(p.id), nm);
                if (p.psoId) psoNameById.set(String(p.psoId), nm);
            });
            const locNameById = new Map<string, string>();
            locs.forEach((l: any) => locNameById.set(String(l.id), String(l.name || 'N/A')));
            const branchById = new Map<string, any>();
            branches.forEach((b: any) => branchById.set(String(b.id), b));

            const psoBranchCountByName = new Map<string, number>();
            const psoLocationsByName = new Map<string, Set<string>>();
            branches.forEach((b: any) => {
                const name = psoNameById.get(String(b.psoId || '')) || 'N/A';
                if (psoOnlyNames.has(name)) {
                    psoBranchCountByName.set(name, (psoBranchCountByName.get(name) || 0) + 1);
                    const locName = locNameById.get(String(b.locationId || '')) || 'N/A';
                    const existingLocs = psoLocationsByName.get(name) || new Set<string>();
                    existingLocs.add(locName);
                    psoLocationsByName.set(name, existingLocs);
                }
            });
            setSummaryPsoBranchCounts(Object.fromEntries(psoBranchCountByName.entries()));
            setSummaryPsoLocations(
                Object.fromEntries(
                    Array.from(psoLocationsByName.entries()).map(([name, set]) => [name, Array.from(set).sort((a, b) => a.localeCompare(b)).join(', ') || 'N/A'])
                )
            );

            const visitDocs: any[] = [];
            if (psoIds.length) {
                for (let i = 0; i < psoIds.length; i += 10) {
                    const chunk = psoIds.slice(i, i + 10);
                    if (chunk.length) {
                        const snap = await getDocs(query(collection(db, 'banks', companyId, 'visit'), where('createdBy', 'in', chunk)));
                        visitDocs.push(...snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })));
                    }
                }
            } else if (locationBranchIds.length) {
                for (let i = 0; i < locationBranchIds.length; i += 10) {
                    const chunk = locationBranchIds.slice(i, i + 10);
                    if (chunk.length) {
                        const snap = await getDocs(query(collection(db, 'banks', companyId, 'visit'), where('branchId', 'in', chunk)));
                        visitDocs.push(...snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })));
                    }
                }
            } else if (managedSet) {
                if (managedSet.size > 0) {
                    const managedIds = Array.from(managedSet);
                    for (let i = 0; i < managedIds.length; i += 10) {
                        const chunk = managedIds.slice(i, i + 10);
                        if (chunk.length) {
                            const snap = await getDocs(query(collection(db, 'banks', companyId, 'visit'), where('createdBy', 'in', chunk)));
                            visitDocs.push(...snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })));
                        }
                    }
                }
            } else {
                const snap = await getDocs(collection(db, 'banks', companyId, 'visit'));
                visitDocs.push(...snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })));
            }

            const filteredVisits = locationBranchIds.length
                ? visitDocs.filter((v) => locationBranchIds.includes(String(v.branchId || '')))
                : visitDocs;

            const locationSurveyDocs: any[] = [];
            if (psoIds.length) {
                for (let i = 0; i < psoIds.length; i += 10) {
                    const chunk = psoIds.slice(i, i + 10);
                    if (chunk.length) {
                        const snap = await getDocs(query(collection(db, 'banks', companyId, 'locationSurveys'), where('createdBy', 'in', chunk)));
                        locationSurveyDocs.push(...snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })));
                    }
                }
            } else if (selectedLocation) {
                const snap = await getDocs(
                    query(collection(db, 'banks', companyId, 'locationSurveys'), where('locationAddress', '==', selectedLocation))
                );
                locationSurveyDocs.push(...snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })));
            } else if (managedSet) {
                if (managedSet.size > 0) {
                    const managedIds = Array.from(managedSet);
                    for (let i = 0; i < managedIds.length; i += 10) {
                        const chunk = managedIds.slice(i, i + 10);
                        if (chunk.length) {
                            const snap = await getDocs(
                                query(collection(db, 'banks', companyId, 'locationSurveys'), where('createdBy', 'in', chunk))
                            );
                            locationSurveyDocs.push(...snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })));
                        }
                    }
                }
            } else {
                const snap = await getDocs(collection(db, 'banks', companyId, 'locationSurveys'));
                locationSurveyDocs.push(...snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })));
            }

            const filteredLocationSurveys = selectedLocation
                ? locationSurveyDocs.filter((s) => String(s.locationAddress || 'N/A') === selectedLocation)
                : locationSurveyDocs;

            const summaryMap = new Map<string, SummaryRow>();
            const addSummary = (args: { dt: string; dateTs: number | null; psoName: string; location: string; branchSurvey?: number; newLocationSurvey?: number }) => {
                if (!psoOnlyNames.has(args.psoName)) return;
                const key = `${args.dt}|${args.psoName}|${args.location}`;
                const ex = summaryMap.get(key) || {
                    id: key,
                    date: args.dt,
                    dateTs: args.dateTs,
                    psoName: args.psoName,
                    location: args.location,
                    totalBranches: psoBranchCountByName.get(args.psoName) || 0,
                    branchSurveys: 0,
                    newLocationSurveys: 0,
                };
                ex.branchSurveys += args.branchSurvey || 0;
                ex.newLocationSurveys += args.newLocationSurvey || 0;
                if (args.dateTs != null) ex.dateTs = args.dateTs;
                summaryMap.set(key, ex);
            };

            filteredVisits.forEach((v: any) => {
                const d = toDateValue(v.createdAt) || toDateValue(v.dateOfVisit) || toDateValue(v.surveyDate);
                const b = branchById.get(String(v.branchId || ''));
                addSummary({
                    dt: dateOnly(d),
                    dateTs: d ? d.getTime() : null,
                    psoName: psoNameById.get(String(v.createdBy || '')) || 'N/A',
                    location: b?.locationId ? (locNameById.get(String(b.locationId)) || 'N/A') : 'N/A',
                    branchSurvey: 1,
                });
            });

            filteredLocationSurveys.forEach((s: any) => {
                const d = toDateValue(s.createdAt) || toDateValue(s.surveyDate);
                addSummary({
                    dt: dateOnly(d),
                    dateTs: d ? d.getTime() : null,
                    psoName: psoNameById.get(String(s.createdBy || '')) || 'N/A',
                    location: String(s.locationAddress || 'N/A'),
                    newLocationSurvey: 1,
                });
            });

            const rows = Array.from(summaryMap.values()).sort((a, b) => (b.dateTs || 0) - (a.dateTs || 0));
            setSummaryQueryRows(rows);
            setSummaryPage(1);
        } catch (error) {
            console.error('Summary search query failed:', error);
        } finally {
            setLoadingTables(false);
        }
    };

    const clearSummarySearch = () => {
        setSummarySearchPso('all');
        setSummarySearchLocation('all');
        setSummaryQueryRows(null);
        setSummaryPage(1);
    };

    const handleSummaryColumnSort = (field: 'totalBranches' | 'branchSurveys' | 'newLocationSurveys') => {
        if (summarySortBy !== field) {
            setSummarySortBy(field);
            setSummarySortOrder('desc');
            return;
        }
        if (summarySortOrder === 'desc') {
            setSummarySortOrder('asc');
            return;
        }
        // 3rd click: reset to default ordering (no manual sort)
        setSummarySortBy(null);
        setSummarySortOrder('desc');
    };

    const sortMarker = (field: 'totalBranches' | 'branchSurveys' | 'newLocationSurveys'): string => {
        if (summarySortBy !== field || summarySortBy === null) return '↕';
        return summarySortOrder === 'asc' ? '▲' : '▼';
    };

    const branchColumns: Column<BranchSurveyRow>[] = [
        { header: 'Date', accessor: 'date', width: '12%' },
        { header: 'Survey ID', accessor: 'surveyId', width: '18%' },
        { header: 'Branch', accessor: 'branchCode', width: '14%' },
        { header: 'PSO', accessor: 'psoName', width: '20%' },
        { header: 'Location', accessor: 'location', width: '20%' },
        { header: 'Status', accessor: 'status', width: '10%' },
    ];

    const summaryColumns: Column<SummaryRow>[] = [
        { header: 'Date', accessor: 'date', width: '12%' },
        { header: 'PSO name', accessor: 'psoName', width: '18%' },
        { header: 'Location', accessor: 'location', width: '20%' },
        {
            header: (
                <button type="button" onClick={() => handleSummaryColumnSort('totalBranches')} className="inline-flex items-center gap-1">
                    <span>Total branches</span>
                    <span>{sortMarker('totalBranches')}</span>
                </button>
            ),
            accessor: 'totalBranches',
            width: '17%',
            align: 'right',
        },
        {
            header: (
                <button type="button" onClick={() => handleSummaryColumnSort('branchSurveys')} className="inline-flex items-center gap-1">
                    <span>Branch surveys</span>
                    <span>{sortMarker('branchSurveys')}</span>
                </button>
            ),
            accessor: 'branchSurveys',
            width: '17%',
            align: 'right',
        },
        {
            header: (
                <button type="button" onClick={() => handleSummaryColumnSort('newLocationSurveys')} className="inline-flex items-center gap-1">
                    <span>New location surveys</span>
                    <span>{sortMarker('newLocationSurveys')}</span>
                </button>
            ),
            accessor: 'newLocationSurveys',
            width: '16%',
            align: 'right',
        },
    ];

    const statusPie = useMemo(() => {
        const total = Object.values(data.requestStatus).reduce((a, b) => a + b, 0) || 1;
        let acc = 0;
        const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#14b8a6'];
        const slices = Object.entries(data.requestStatus).map(([k, v], i) => {
            const from = (acc / total) * 100;
            acc += v;
            const to = (acc / total) * 100;
            return `${colors[i % colors.length]} ${from}% ${to}%`;
        });
        return slices.length ? `conic-gradient(${slices.join(', ')})` : 'conic-gradient(#e5e7eb 0% 100%)';
    }, [data.requestStatus]);

    const coverageChartData = useMemo(() => {
        const totalBranches = data.kpis.branches || 0;
        const dayMap = new Map<string, Set<string>>();

        data.branchSurveyRows.forEach((row) => {
            if (!row.dateTs || !row.branchCode || row.branchCode === 'N/A') return;
            const dt = dateOnly(new Date(row.dateTs));
            const existing = dayMap.get(dt) || new Set<string>();
            existing.add(row.branchCode);
            dayMap.set(dt, existing);
        });

        const today = new Date();
        const recentDates = Array.from({ length: 7 }).map((_, idx) => {
            const d = new Date(today.getFullYear(), today.getMonth(), today.getDate());
            d.setDate(d.getDate() - (6 - idx));
            return dateOnly(d);
        });

        return recentDates.map((date) => {
            const surveyed = dayMap.get(date)?.size || 0;
            return {
                date,
                surveyed,
                remaining: Math.max(totalBranches - surveyed, 0),
            };
        });
    }, [data.branchSurveyRows, data.kpis.branches]);

    const monthlyCoveragePieData = useMemo(() => {
        const totalBranches = data.kpis.branches || 0;
        const [yearStr, monthStr] = coverageMonth.split('-');
        const year = Number(yearStr);
        const monthIndex = Number(monthStr) - 1;
        const monthStart = new Date(year, monthIndex, 1);
        const monthEnd = new Date(year, monthIndex + 1, 0, 23, 59, 59, 999);
        const monthStartTs = monthStart.getTime();
        const monthEndTs = monthEnd.getTime();
        const coveredBranchCodes = new Set<string>();

        data.branchSurveyRows.forEach((row) => {
            if (!row.dateTs || !row.branchCode || row.branchCode === 'N/A') return;
            if (row.dateTs < monthStartTs || row.dateTs > monthEndTs) return;
            coveredBranchCodes.add(row.branchCode);
        });

        const covered = coveredBranchCodes.size;
        const remaining = Math.max(totalBranches - covered, 0);
        return [
            { name: 'Covered this month', value: covered },
            { name: 'Not covered', value: remaining },
        ];
    }, [coverageMonth, data.branchSurveyRows, data.kpis.branches]);

    const coverageMonthOptions = useMemo(() => {
        const months = new Set<string>();
        const now = new Date();
        months.add(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
        data.branchSurveyRows.forEach((row) => {
            if (!row.dateTs) return;
            const d = new Date(row.dateTs);
            months.add(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
        });
        return Array.from(months).sort((a, b) => b.localeCompare(a));
    }, [data.branchSurveyRows]);

    const coverageMonthLabel = useMemo(() => {
        const [y, m] = coverageMonth.split('-').map(Number);
        const d = new Date(y, (m || 1) - 1, 1);
        return d.toLocaleString(undefined, { month: 'long', year: 'numeric' });
    }, [coverageMonth]);

    const monthlyCoverageTotals = useMemo(() => {
        const covered = monthlyCoveragePieData.find((d) => d.name === 'Covered this month')?.value || 0;
        const notCovered = monthlyCoveragePieData.find((d) => d.name === 'Not covered')?.value || 0;
        return {
            total: covered + notCovered,
            covered,
            notCovered,
        };
    }, [monthlyCoveragePieData]);

    const isInRange = (dateTs: number | null, tab: RangeKey): boolean => {
        if (tab === 'all') return true;
        const start = rangeStart(tab);
        const end = rangeEnd(tab);
        if (!start) return true;
        if (dateTs == null) return false;
        if (dateTs < start.getTime()) return false;
        if (end && dateTs > end.getTime()) return false;
        return true;
    };

    const branchFiltered = useMemo(
        () => data.branchSurveyRows.filter((r) => isInRange(r.dateTs, branchTab)),
        [data.branchSurveyRows, branchTab]
    );
    const summarySourceRows = summaryQueryRows ?? data.summaryRows;
    const summaryFiltered = useMemo(
        () => summarySourceRows.filter((r) => isInRange(r.dateTs, summaryTab)),
        [summarySourceRows, summaryTab]
    );

    const branchDisplayRows = branchFiltered;
    const summaryDisplayRows = useMemo(() => {
        const base = summaryFiltered;
        const now = new Date();
        const defaultDate = dateOnly(now);
        const defaultTs = now.getTime();
        const targetPsoNames =
            summarySearchPso !== 'all'
                ? [summarySearchPso]
                : summaryPsoOptions;

        const existing = new Set(base.map((r) => r.psoName));
        const zeroRows: SummaryRow[] = targetPsoNames
            .filter((name) => !existing.has(name))
            .map((name) => ({
                id: `zero|${summaryTab}|${summarySearchLocation}|${name}`,
                date: defaultDate,
                dateTs: defaultTs,
                psoName: name,
                location: summarySearchLocation === 'all' ? (summaryPsoLocations[name] || 'N/A') : summarySearchLocation,
                totalBranches: summaryPsoBranchCounts[name] || 0,
                branchSurveys: 0,
                newLocationSurveys: 0,
            }));

        return [...base, ...zeroRows];
    }, [summaryFiltered, summarySearchPso, summaryPsoOptions, summaryTab, summarySearchLocation, summaryPsoBranchCounts, summaryPsoLocations]);

    const summarySortedRows = useMemo(() => {
        const rows = [...summaryDisplayRows];
        if (!summarySortBy) {
            rows.sort((a, b) => (b.dateTs || 0) - (a.dateTs || 0));
            return rows;
        }
        rows.sort((a, b) => {
            const delta = Number(a[summarySortBy]) - Number(b[summarySortBy]);
            return summarySortOrder === 'asc' ? delta : -delta;
        });
        return rows;
    }, [summaryDisplayRows, summarySortBy, summarySortOrder]);

    const perPage = 10;
    const branchTotalPages = Math.max(1, Math.ceil(branchDisplayRows.length / perPage));
    const summaryTotalPages = Math.max(1, Math.ceil(summarySortedRows.length / perPage));
    const branchPaginated = branchDisplayRows.slice((branchPage - 1) * perPage, branchPage * perPage);
    const summaryPaginated = summarySortedRows.slice((summaryPage - 1) * perPage, summaryPage * perPage);

    const filteredRegions = useMemo(() => {
        if (!selectedKpi || selectedKpi.type !== 'regions') return [];
        const list = data.rawLocs.filter(loc => {
            if (managedPsoAuthIds !== undefined) {
                return data.rawBranches.some(b => b.locationId === loc.id);
            }
            return true;
        });
        const counted = list.map(loc => {
            const count = data.rawBranches.filter(b => b.locationId === loc.id).length;
            return {
                id: loc.id,
                name: loc.name || 'N/A',
                branchCount: count
            };
        });
        return counted.filter(loc =>
            loc.name.toLowerCase().includes(modalSearch.toLowerCase()) ||
            loc.id.toLowerCase().includes(modalSearch.toLowerCase())
        );
    }, [data.rawLocs, data.rawBranches, managedPsoAuthIds, selectedKpi, modalSearch]);

    const filteredPsos = useMemo(() => {
        if (!selectedKpi) return [];
        let list = data.rawPsos;
        if (selectedKpi.type === 'pso') {
            list = list.filter(p => isPsoDesignation(p.designation) && !isInchargeDesignation(p.designation));
        } else if (selectedKpi.type === 'incharges') {
            list = list.filter(p => isInchargeDesignation(p.designation));
        } else if (selectedKpi.type === 'regional_head') {
            list = list.filter(p => isRegionalDesignation(p.designation));
        } else {
            return [];
        }
        const counted = list.map(p => {
            const count = data.rawBranches.filter(b => b.psoId === p.psoId || b.psoId === p.id).length;
            return {
                id: p.id,
                psoId: p.psoId || 'N/A',
                name: p.name || 'N/A',
                email: p.email || 'N/A',
                contact: p.contact || 'N/A',
                designation: p.designation || 'N/A',
                branchCount: count
            };
        });
        return counted.filter(p =>
            p.name.toLowerCase().includes(modalSearch.toLowerCase()) ||
            p.email.toLowerCase().includes(modalSearch.toLowerCase()) ||
            p.designation.toLowerCase().includes(modalSearch.toLowerCase()) ||
            p.psoId.toLowerCase().includes(modalSearch.toLowerCase())
        );
    }, [data.rawPsos, data.rawBranches, selectedKpi, modalSearch]);

    const filteredBranchesByType = useMemo(() => {
        if (!selectedKpi || selectedKpi.type !== 'branch_type') return [];
        const label = selectedKpi.label;
        const list = data.rawBranches.filter(b => {
            const bLabel = branchTypeKpiLabel(b);
            if (label === 'Other') {
                return bLabel.startsWith('Other');
            }
            return bLabel === label;
        });
        return list.filter(b =>
            (b.branchCode || '').toLowerCase().includes(modalSearch.toLowerCase()) ||
            (b.address || '').toLowerCase().includes(modalSearch.toLowerCase()) ||
            (b.locationName || '').toLowerCase().includes(modalSearch.toLowerCase()) ||
            (b.psoName || '').toLowerCase().includes(modalSearch.toLowerCase())
        );
    }, [data.rawBranches, selectedKpi, modalSearch]);

    const branchTypeKpiRows = useMemo(
        () => branchTypeKpiOrderedRows(data.kpis.branchesByType ?? {}),
        [data.kpis.branchesByType]
    );

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{dashboardTitle}</h2>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    {dashboardSubtitle ?? `Welcome, ${pso?.name || 'Head'}.`}
                </p>
            </div>

            {loadingKpi ? (
                <div className="space-y-4">
                    {!hideStaffKpis && (
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                            {Array.from({ length: 4 }).map((_, i) => (
                                <div key={i} className="h-24 animate-pulse rounded-xl bg-gray-200 dark:bg-gray-700" />
                            ))}
                        </div>
                    )}
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                        {Array.from({ length: 8 }).map((_, i) => (
                            <div key={`t-${i}`} className="h-20 animate-pulse rounded-xl bg-gray-200 dark:bg-gray-700" />
                        ))}
                    </div>
                </div>
            ) : (
                <div className="space-y-4">
                    {!hideStaffKpis && (
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                            {[
                                ['Total Regions', data.kpis.regions, 'regions'],
                                ['Total PSO', data.kpis.staffPso, 'pso'],
                                ['Total Incharges', data.kpis.staffIncharges, 'incharges'],
                                ['Total Regional Head', data.kpis.staffRegional, 'regional_head'],
                            ].map(([label, value, type]) => (
                                <div
                                    key={String(label)}
                                    onClick={() => {
                                        setSelectedKpi({ type: type as any, label: String(label) });
                                        setModalSearch('');
                                        setModalPage(1);
                                    }}
                                    className="cursor-pointer hover:border-blue-500 hover:ring-1 hover:ring-blue-500 transition-all rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-black/30"
                                >
                                    <div className="text-sm text-gray-500 dark:text-gray-400">{label}</div>
                                    <div className="mt-2 text-3xl font-bold text-gray-900 dark:text-gray-100">{value}</div>
                                </div>
                            ))}
                        </div>
                    )}
                    <div>
                        <h3 className="mb-2 text-sm font-semibold text-gray-800 dark:text-gray-200">Locations by type</h3>
                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                            {branchTypeKpiRows.map((row, idx) => {
                                const pct = data.kpis.branches > 0 ? (row.count / data.kpis.branches) * 100 : 0;
                                const grad = BRANCH_TYPE_KPI_GRADIENTS[idx % BRANCH_TYPE_KPI_GRADIENTS.length];
                                return (
                                    <div
                                        key={row.label}
                                        onClick={() => {
                                            setSelectedKpi({ type: 'branch_type', label: row.label });
                                            setModalSearch('');
                                            setModalPage(1);
                                        }}
                                        className="cursor-pointer hover:border-blue-500 hover:ring-1 hover:ring-blue-500 transition-all relative overflow-hidden rounded-xl border border-gray-200 bg-white p-4 shadow-sm ring-1 ring-gray-100 dark:border-gray-700 dark:bg-black/30 dark:ring-white/5"
                                    >
                                        <div className={`absolute left-0 top-0 h-full w-1 bg-gradient-to-b ${grad}`} aria-hidden />
                                        <div className="pl-2">
                                            <div className="line-clamp-2 text-xs font-medium text-gray-600 dark:text-gray-400">{row.label}</div>
                                            <div className="mt-1 text-2xl font-bold tabular-nums text-gray-900 dark:text-gray-100">{row.count}</div>
                                            <div className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">{pct.toFixed(1)}% of total branches</div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {loadingCharts ? (
                <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                    <div className="h-56 animate-pulse rounded-xl bg-gray-200 dark:bg-gray-700" />
                    <div className="h-56 animate-pulse rounded-xl bg-gray-200 dark:bg-gray-700" />
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                    <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-black/30">
                        <h3 className="mb-4 text-sm font-semibold text-gray-800 dark:text-gray-200">Weekly Survey Summary</h3>
                        <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
                            Unique branch coverage by day for the last 7 days against total branches ({data.kpis.branches}).
                        </p>
                        <div className="h-64 w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={coverageChartData} margin={{ top: 8, right: 8, left: 0, bottom: 24 }}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="date" angle={-35} textAnchor="end" interval="preserveStartEnd" height={48} tick={{ fontSize: 10 }} />
                                    <YAxis allowDecimals={false} />
                                    <Tooltip />
                                    <Legend />
                                    <ReferenceLine
                                        y={data.kpis.branches || 0}
                                        stroke="#6b7280"
                                        strokeDasharray="4 4"
                                        label={{ value: `Total branches: ${data.kpis.branches || 0}`, position: 'insideTopRight' }}
                                    />
                                    <Bar dataKey="surveyed" name="Surveyed branches" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                    <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-black/30">
                        <h3 className="mb-4 text-sm font-semibold text-gray-800 dark:text-gray-200">Monthly Branch Coverage</h3>
                        <div className="mb-3 flex items-center gap-2">
                            <span className="text-xs text-gray-500 dark:text-gray-400">Month:</span>
                            <select
                                value={coverageMonth}
                                onChange={(e) => setCoverageMonth(e.target.value)}
                                className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
                            >
                                {coverageMonthOptions.map((monthValue) => {
                                    const [y, m] = monthValue.split('-').map(Number);
                                    const d = new Date(y, (m || 1) - 1, 1);
                                    const label = d.toLocaleString(undefined, { month: 'long', year: 'numeric' });
                                    return (
                                        <option key={monthValue} value={monthValue}>{label}</option>
                                    );
                                })}
                            </select>
                        </div>
                        <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
                            Deduplicated by branch for {coverageMonthLabel}: multiple visits to the same branch count once.
                        </p>
                        <div className="mb-3 flex flex-wrap gap-3 text-sm">
                            <span className="rounded-md bg-indigo-100 px-2.5 py-1 font-medium text-indigo-700">Total branches: {monthlyCoverageTotals.total}</span>
                            <span className="rounded-md bg-blue-100 px-2.5 py-1 font-medium text-blue-700">Covered: {monthlyCoverageTotals.covered}</span>
                            <span className="rounded-md bg-rose-100 px-2.5 py-1 font-medium text-rose-700">Not covered: {monthlyCoverageTotals.notCovered}</span>
                        </div>
                        <div className="h-64 w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={monthlyCoveragePieData}
                                        dataKey="value"
                                        nameKey="name"
                                        outerRadius={92}
                                        innerRadius={50}
                                        paddingAngle={2}
                                        label={({ name, value }) => `${name}: ${value}`}
                                    >
                                        {monthlyCoveragePieData.map((entry, idx) => (
                                            <Cell key={`${entry.name}-${idx}`} fill={idx === 0 ? '#2563eb' : '#f43f5e'} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>
            )}

            {loadingTables ? (
                <div className="space-y-4">
                    <div className="h-48 animate-pulse rounded-xl bg-gray-200 dark:bg-gray-700" />
                    <div className="h-48 animate-pulse rounded-xl bg-gray-200 dark:bg-gray-700" />
                </div>
            ) : (
                <>
                    <Table
                        title="Branch Surveys"
                        description="All branch surveys."
                        data={branchPaginated}
                        columns={branchColumns}
                        keyExtractor={(r) => r.id}
                        emptyMessage="No branch surveys found."
                        headerActions={
                            <div className="flex flex-wrap gap-2">
                                {(['daily', 'weekly', 'monthly', 'all'] as RangeKey[]).map((r) => (
                                    <button
                                        key={r}
                                        onClick={() => setBranchTab(r)}
                                        className={`rounded-lg px-3 py-1.5 text-sm font-medium ${branchTab === r ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 border border-gray-300 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600'}`}
                                    >
                                        {r[0].toUpperCase() + r.slice(1)}
                                    </button>
                                ))}
                            </div>
                        }
                        pagination={{
                            currentPage: branchPage,
                            totalPages: branchTotalPages,
                            onPageChange: setBranchPage,
                        }}
                    />
                    <Table
                        title="Survey Summary"
                        description="Date, PSO, location, assigned branches, and survey split."
                        data={summaryPaginated}
                        columns={summaryColumns}
                        keyExtractor={(r) => r.id}
                        emptyMessage="No summary records found."
                        headerActions={
                            <div className="flex w-full flex-col gap-2">
                                <div className="flex flex-wrap gap-2">
                                    {(['daily', 'weekly', 'monthly', 'all'] as RangeKey[]).map((r) => (
                                        <button
                                            key={r}
                                            onClick={() => setSummaryTab(r)}
                                            className={`rounded-lg px-3 py-1.5 text-sm font-medium ${summaryTab === r ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 border border-gray-300 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600'}`}
                                        >
                                            {r[0].toUpperCase() + r.slice(1)}
                                        </button>
                                    ))}
                                </div>
                                <div className="flex flex-wrap items-center gap-2">
                                    <select
                                        value={summarySearchPso}
                                        onChange={(e) => setSummarySearchPso(e.target.value)}
                                        className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
                                    >
                                        <option value="all">All PSO</option>
                                        {summaryPsoOptions.map((name) => (
                                            <option key={name} value={name}>{name}</option>
                                        ))}
                                    </select>
                                    <select
                                        value={summarySearchLocation}
                                        onChange={(e) => setSummarySearchLocation(e.target.value)}
                                        className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
                                    >
                                        <option value="all">All Location</option>
                                        {summaryLocationOptions.map((name) => (
                                            <option key={name} value={name}>{name}</option>
                                        ))}
                                    </select>
                                    <button
                                        onClick={() => void handleSummarySearch()}
                                        className="rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white"
                                    >
                                        Search
                                    </button>
                                    {(summarySearchPso !== 'all' || summarySearchLocation !== 'all' || summaryQueryRows !== null) && (
                                        <button
                                            onClick={clearSummarySearch}
                                            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
                                        >
                                            Clear
                                        </button>
                                    )}
                                </div>
                            </div>
                        }
                        pagination={{
                            currentPage: summaryPage,
                            totalPages: summaryTotalPages,
                            onPageChange: setSummaryPage,
                        }}
                    />
                </>
            )}

            {/* KPI Details Modal */}
            <Modal
                isOpen={selectedKpi !== null}
                onClose={() => {
                    setSelectedKpi(null);
                    setModalSearch('');
                    setModalPage(1);
                }}
                title={selectedKpi ? `${selectedKpi.label} Details` : ''}
                size="xl"
            >
                {selectedKpi && (
                    <div className="space-y-4 p-6">
                        {/* Search bar inside modal */}
                        <div className="flex max-w-xs items-center rounded-lg border border-gray-300 bg-white px-3 py-2 dark:border-gray-700 dark:bg-gray-800">
                            <Icon icon={FiSearch} className="mr-2 h-4 w-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search..."
                                value={modalSearch}
                                onChange={(e) => {
                                    setModalSearch(e.target.value);
                                    setModalPage(1);
                                }}
                                className="w-full bg-transparent text-sm text-gray-900 outline-none dark:text-gray-100 placeholder-gray-400"
                            />
                        </div>

                        {selectedKpi.type === 'regions' && (
                            <Table
                                data={filteredRegions.slice((modalPage - 1) * 10, modalPage * 10)}
                                columns={[
                                    { header: 'Region Name', accessor: 'name', width: '80%' },
                                    { header: 'Total Branches', accessor: 'branchCount', width: '20%', align: 'right' }
                                ]}
                                keyExtractor={(r) => r.id}
                                pagination={{
                                    currentPage: modalPage,
                                    totalPages: Math.max(1, Math.ceil(filteredRegions.length / 10)),
                                    onPageChange: setModalPage
                                }}
                                emptyMessage="No regions found."
                            />
                        )}

                        {(selectedKpi.type === 'pso' || selectedKpi.type === 'incharges' || selectedKpi.type === 'regional_head') && (
                            <Table
                                data={filteredPsos.slice((modalPage - 1) * 10, modalPage * 10)}
                                columns={[
                                    { header: 'ID', accessor: 'psoId', width: '15%' },
                                    { header: 'Name', accessor: 'name', width: '25%' },
                                    { header: 'Email', accessor: 'email', width: '25%' },
                                    { header: 'Contact', accessor: 'contact', width: '15%' },
                                    { header: 'Designation', accessor: 'designation', width: '10%' },
                                    { header: 'Branches', accessor: 'branchCount', width: '10%', align: 'right' }
                                ]}
                                keyExtractor={(r) => r.id}
                                pagination={{
                                    currentPage: modalPage,
                                    totalPages: Math.max(1, Math.ceil(filteredPsos.length / 10)),
                                    onPageChange: setModalPage
                                }}
                                emptyMessage="No staff records found."
                            />
                        )}

                        {selectedKpi.type === 'branch_type' && (
                            <Table
                                data={filteredBranchesByType.slice((modalPage - 1) * 10, modalPage * 10)}
                                columns={[
                                    {
                                        header: 'Branch',
                                        width: '33%',
                                        render: (branch) => (
                                            <div className="space-y-1.5 text-sm">
                                                <div>
                                                    <span className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                                        Code{' '}
                                                    </span>
                                                    <span className="font-semibold text-gray-900 dark:text-gray-100">{branch.branchCode}</span>
                                                </div>
                                                <div
                                                    style={{
                                                        maxWidth: '280px',
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: 'vertical',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'normal',
                                                    }}
                                                    className="text-gray-700 dark:text-gray-300"
                                                >
                                                    {branch.address}
                                                </div>
                                                <div>
                                                    <span className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                                        Branch Contact No.{' '}
                                                    </span>
                                                    <span className="text-gray-600 dark:text-gray-400">{branch.mobile}</span>
                                                </div>
                                            </div>
                                        ),
                                    },
                                    {
                                        header: 'Location & position',
                                        width: '32%',
                                        render: (branch) => {
                                            let lat = '';
                                            let lng = '';
                                            let distance = '';
                                            if (branch.location && (branch.location.lat != null || branch.location.long != null)) {
                                                if (branch.location.lat != null) lat = String(branch.location.lat);
                                                if (branch.location.long != null) lng = String(branch.location.long);
                                                distance = (branch.location.distanceFromRegion || '').trim();
                                            }
                                            if ((!lat || !lng) && branch.latitudeLongitude) {
                                                const parts = branch.latitudeLongitude.split(/[,\s]+/).map((p: any) => p.trim()).filter(Boolean);
                                                if (parts.length >= 2) {
                                                    lat = lat || parts[0];
                                                    lng = lng || parts[1];
                                                }
                                            }
                                            if (!distance && branch.distanceFromRegion) distance = String(branch.distanceFromRegion).trim();
                                            const coord = lat && lng ? `${Number(lat).toFixed(5)}, ${Number(lng).toFixed(5)}` : '—';
                                            return (
                                                <div className="space-y-1.5 text-sm">
                                                    <div className="font-medium text-gray-800 dark:text-gray-200">
                                                        {branch.locationName || '—'}
                                                    </div>
                                                    <div className="font-mono text-xs tabular-nums text-gray-600 dark:text-gray-400">
                                                        <span className="text-gray-500 dark:text-gray-500">Lat, Long </span>
                                                        {coord}
                                                    </div>
                                                    {distance ? (
                                                        <div className="text-gray-600 dark:text-gray-400">
                                                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Distance </span>
                                                            {distance}
                                                        </div>
                                                    ) : null}
                                                </div>
                                            );
                                        },
                                    },
                                    {
                                        header: 'PSO Details',
                                        width: '20%',
                                        render: (branch) => (
                                            <div className="flex flex-col text-[11px] leading-snug sm:text-xs text-gray-600 dark:text-gray-400">
                                                <span className="font-semibold text-gray-800 dark:text-gray-200">
                                                    {branch.psoName && branch.psoName !== 'N/A' ? branch.psoName : 'No PSO'}
                                                </span>
                                                {branch.psoEmail && branch.psoEmail !== 'N/A' && (
                                                    <span className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
                                                        {branch.psoEmail}
                                                    </span>
                                                )}
                                                {branch.psoContact && branch.psoContact !== 'N/A' && (
                                                    <span className="text-[10px] text-gray-500 dark:text-gray-400">
                                                        {branch.psoContact}
                                                    </span>
                                                )}
                                            </div>
                                        )
                                    },
                                    {
                                        header: 'Status',
                                        align: 'center',
                                        width: '15%',
                                        render: (branch) => (
                                            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${branch.isActive !== false
                                                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                                                : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                                                }`}>
                                                {branch.isActive !== false ? 'Active' : 'Inactive'}
                                            </span>
                                        )
                                    }
                                ]}
                                keyExtractor={(r) => r.id}
                                pagination={{
                                    currentPage: modalPage,
                                    totalPages: Math.max(1, Math.ceil(filteredBranchesByType.length / 10)),
                                    onPageChange: setModalPage
                                }}
                                emptyMessage="No branches found for this type."
                            />
                        )}
                    </div>
                )}
            </Modal>
        </div>
    );
}

